import {
	buildFetchSearchInPlaceHttpErrorMessage,
	FETCH_HOTELS_ERROR_MESSAGE,
	HOTELS_DATA_NOT_FOUND_MESSAGE,
} from '../../../utils/sentry-errors'
import {
	captureException,
	captureMessage,
	init,
	setContext,
	setTags,
} from '@sentry/nextjs'
import axios, { AxiosError, AxiosResponse } from 'axios'
import axiosRetry from 'axios-retry'
import { Currencies } from 'currencies-map'
import { addDays, addWeeks, format } from 'date-fns'
import { startCase } from 'lodash'
import {
	DATE_FORMAT_QUERY,
	DEFAULT_ALLOCATION,
	DEFAULT_CHECK_IN_DATE,
	DEFAULT_CHECK_OUT_DATE,
	FACILITIES,
	FALLBACK_IP_ADDRESS,
	HOTEL_DEFAULT_CHECK_IN_WEEKS,
	HOTEL_DEFAULT_STAY_DAYS,
	INIT_DATA_STORAGE_KEY,
	STARS,
	STAY_OPTIONS,
	userAgents,
} from '../constants'
import { getLanguageByLang } from '../languages'
import {
	CreateSearchParams,
	DestPagesProps,
	HotelsListResponse,
	SearchInPlaceRequestParams,
	SearchInPlaceRequestResponse,
	SearchPlaceType,
	SORT_BY
} from '../types'
import {
	Filter,
	HotelAmenitiesFilter,
	HotelSearchCreateRequest,
	HotelSearchPollRequest,
	HotelSearchResponse,
	HotelSearchResponseStatusEnum,
	StarsFilter
} from '../types/skyscanner-wrapper-api'
import {
	determineSearchPlaceFromPath,
	formatNumber,
	hasValidDates,
	isBotUA,
	isLandmark,
	needsHotelDateFallback,
	parseSearchParamsToLower,
	randomizeUserAgents,
} from '../utils'
import {
	calcNightsCount,
	createSearchParamsToKey,
	dateStringToObj,
	getUserRating,
	isSessionExpiredError,
	mnMxToPriceRangeFilter,
	paramStringToEnumArray,
	polygonToGeoAreaFilter,
	roomParamToAdultsChildren,
} from '../utils/hotelApiUtils'
import {
	convertSkyscannerToKayakFormat,
	mapSortParamsToSort,
} from '../utils/skyscanner-to-kayak-converter'

const API_URL = process.env.NEXT_PUBLIC_SKYSCANNER_WRAPPER_API_URL
const CREATE_URL = `${API_URL}/api/v1/hotels/search/create`

/**
 *  Maximum create sessions to store.
 * 	1 would be enough, increasing just to avoid weird bugs in the future
 */
const MAX_SESSIONS = 3

const DEFAULT_PAGE = 0
const PAGE_SIZE = 25

const axiosInstanceCreate = axios.create()
axiosRetry(axiosInstanceCreate, {
	retries: 4,
	retryDelay: (retryCount) => {
		console.log(`retry attempt (createSearchInPlace): ${retryCount}`)
		return 2000
	},
	retryCondition(error) {
		if (error.status && error.status <= 404) {
			return false
		}
		return true
	},
})

const axiosInstancePoll = axios.create()
axiosRetry(axiosInstancePoll, {
	retries: 4,
	retryDelay: (retryCount) => {
		console.log(`retry attempt (pollSearchInPlace): ${retryCount}`)
		return 2000
	},
	retryCondition(error) {
		if (isSessionExpiredError(error)) return false
		if (error.response?.status && error.response.status <= 404) {
			return false
		}
		return true
	},
})

// Assuming there is only one session at a time

//** Search sessions response promises */
let sessionResponses = new Map<string, Promise<HotelSearchResponse | null>>()

// Updated version
export async function fetchSearchInPlace(
	params: SearchInPlaceRequestParams
): Promise<SearchInPlaceRequestResponse | null> {
	init({
		dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
		debug: false,
		tracesSampleRate: 0,
		replaysOnErrorSampleRate: 0,
		replaysSessionSampleRate: 0,
		environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
	})

	const bareParams = JSON.stringify(params)

	setContext('Request Params', {
		searchParams: bareParams,
		CREATE_URL,
	})

	setTags(JSON.parse(bareParams))

	const { market, locale } = getLanguageByLang(params.language)
	const {
		currency = 'EUR', // TODO: define default currency
		placeID = '',
		checkIn,
		checkOut,
		roomAllocation,
	} = params
	const createParams: CreateSearchParams = {
		market,
		locale,
		currency,
		entityId: placeID,
		checkinDate: dateStringToObj(checkIn!),
		checkoutDate: dateStringToObj(checkOut!),
		...roomAllocation,
	}

	try {
		const createResponse = await createSearch(createParams)
		if (createResponse === null) return null

		return pollSearch(createParams, params, createResponse)
	} catch (e: any) {
		setContext('catch fetchSearchInPlace', {
			error: e,
		})

		console.log('Error Message:', e.message)
		console.log('Error in fetchSearchInPlace:', e)
		captureException(`Error in fetchSearchInPlace: ${e.message}`)
	}
	return null
}

export async function createSearch(
	params: CreateSearchParams
): Promise<HotelSearchResponse | null> {
	const key = createSearchParamsToKey(params)
	let savedSearchPromise: Promise<HotelSearchResponse | null> | undefined =
		sessionResponses.get(key)
	if (savedSearchPromise) return savedSearchPromise

	let searchPromise = (async () => {
		const createRequest: HotelSearchCreateRequest = {
			query: {
				...params,
			},
			initialPageSize: PAGE_SIZE,
		}
		const createResponse = await axiosInstanceCreate(CREATE_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			data: JSON.stringify(createRequest),
		})
		if (
			!createResponse.status ||
			createResponse.status < 200 ||
			createResponse.status >= 300
		) {
			setContext('fetchSearchInPlace response with error', {
				createResponse,
			})

			console.error(`HTTP error! status: ${createResponse.status}`)
			captureException(
				buildFetchSearchInPlaceHttpErrorMessage(createResponse.status)
			)
			return null
		}
		// TODO: Process errors correctly
		const { sessionToken } = await createResponse.data
		if (!sessionToken) {
			console.error(`HTTP error! status: ${createResponse.status}`)
			captureException(
				`Error in fetchSearchInPlace create: HTTP ${createResponse.status}`
			)
		}
		return (await createResponse.data) as HotelSearchResponse
	})()
	sessionResponses.set(key, searchPromise)
	if (sessionResponses.size > MAX_SESSIONS)
		sessionResponses.delete(sessionResponses.keys().next().value!)
	return searchPromise
}

export async function pollSearch(
	createParams: CreateSearchParams,
	params: SearchInPlaceRequestParams,
	createResponse: HotelSearchResponse
): Promise<SearchInPlaceRequestResponse | null> {
	const { sessionToken } = createResponse

	const nights = calcNightsCount(params.checkIn, params.checkOut)
	const filters: Filter[] = []
	const accommodationTypeFilter = paramStringToEnumArray(params.p, STAY_OPTIONS)
	if (accommodationTypeFilter) filters.push({ accommodationTypeFilter })
	const priceRangeFilter = mnMxToPriceRangeFilter(nights, params.mn, params.mx)
	if (priceRangeFilter) filters.push({ priceRangeFilter })
	const hotelAmenitiesFilter = paramStringToEnumArray<HotelAmenitiesFilter>(
		params.f,
		FACILITIES
	)
	if (hotelAmenitiesFilter) filters.push({ hotelAmenitiesFilter })
	const geoAreaFilter = polygonToGeoAreaFilter(params.polygon)
	if (geoAreaFilter) filters.push({ geoAreaFilter })
	const starsFilter = paramStringToEnumArray<StarsFilter>(params.s, STARS)
	if (starsFilter) filters.push({ starsFilter })

	const sort = mapSortParamsToSort(params.sort?.by, params.sort?.dir)

	// TODO: Result can be ready on create. Check if there is an acceptable sorting option in response
	const pollUrl = `${API_URL}/api/v1/hotels/search/poll/${sessionToken}`
	const pollRequest: HotelSearchPollRequest = {
		pagination: {
			limit: PAGE_SIZE,
			offset: (params.page || 0) * PAGE_SIZE,
		},
		filters: filters.length ? filters : undefined,
		sort,
	}
	let pollResponse: AxiosResponse<any, any>
	try {
		pollResponse = await axiosInstancePoll(pollUrl, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			data: JSON.stringify(pollRequest),
		})
	} catch (error) {
		if (isSessionExpiredError(error as AxiosError)) {
			sessionResponses.delete(createSearchParamsToKey(createParams))
			return fetchSearchInPlace(params)
		} else {
			throw error
		}
	}
	if (
		!pollResponse.status ||
		pollResponse.status < 200 ||
		pollResponse.status >= 300
	) {
		setContext('fetchSearchInPlace response with error', {
			createResponse,
		})

		console.error(`HTTP error! status: ${pollResponse.status}`)
		captureException(
			`Error in fetchSearchInPlace poll: HTTP ${pollResponse.status}`
		)
		return null
	}
	const pollData = pollResponse.data as HotelSearchResponse
	const pollStatus = pollData.status as HotelSearchResponseStatusEnum
	const pollContent = pollData.content

	if (
		pollStatus === HotelSearchResponseStatusEnum.RESULT_STATUS_FAILED ||
		pollStatus === HotelSearchResponseStatusEnum.RESULT_STATUS_UNSPECIFIED
	) {
		setContext('fetchSearchInPlace response with error', {
			createResponse,
		})

		console.error(`HTTP error! status: ${createResponse.status}`)
		captureException(
			`Error in fetchSearchInPlace poll: HTTP ${createResponse.status}`
		)
		return null
	}

	// TODO: Tidy up conversion
	const data = convertSkyscannerToKayakFormat(
		pollContent!,
		nights,
		undefined,
		params.currency,
		params.page,
		PAGE_SIZE,
		pollStatus === HotelSearchResponseStatusEnum.RESULT_STATUS_COMPLETE
	)

	if (data?.complete && data.hotels?.length === 0) {
		captureMessage(HOTELS_DATA_NOT_FOUND_MESSAGE)
		setContext('Response Data', data)
	}
	return data
}

export async function getHotelsData(
	params: DestPagesProps,
	skipFilters?: boolean,
	excludedHotels?: string[]
): Promise<HotelsListResponse> {
	const sessionID = crypto.randomUUID()
	let userAgent = params.searchParams.userAgent || navigator.userAgent

	const [c, r, ci, h, p] = params?.params?.dest

	const pathname = window.location.pathname
	const hasHotelInPath = pathname.includes('/hotel/')

	const country = !!c ? startCase(decodeURIComponent(c)) : ''
	const region = !!r ? startCase(decodeURIComponent(r)) : ''
	const city = !!ci ? startCase(decodeURIComponent(ci)) : ''
	const hotel = !!h ? decodeURIComponent(h) : undefined
	const place = p ? startCase(decodeURIComponent(p)) : ''

	const {
		room,
		page,
		s: stars,
		gr: guestRating,
		p: property,
		fa: facilities,
		ap: allowedPartners,
		t: themes,
		h: hotelChain,
		mn: minPrice,
		mx: maxPrice,
		by,
		dir,
		polygon,
	} = params.searchParams

	let allocations = [DEFAULT_ALLOCATION]

	if (userAgent && isBotUA(userAgent)) {
		userAgent = randomizeUserAgents(userAgents)
	}

	// No all the links in examples follow these rules
	const { type, name } = determineSearchPlaceFromPath(
		[country, region, city, hotel as string, place],
		hasHotelInPath
	)

	const roomAllocation = roomParamToAdultsChildren(room)

	let result: SearchInPlaceRequestResponse | null = null

	const searchParams = parseSearchParamsToLower(params.searchParams)
	const onlyComplete = Boolean(searchParams?.onlycomplete)

	const currency = searchParams?.currency
	const timezone =
		searchParams?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
	// const latlon = searchParams?.latlon
	const utcOffset = searchParams?.['utcoffsetminutes']
		? Number(searchParams['utcoffsetminutes'])
		: new Date().getTimezoneOffset() * -1

	// made case insensitive in case of human error
	const hotelID = searchParams?.['hotelid']
	const placeID = searchParams?.['placeid']

	const isHotel = type === SearchPlaceType.HOTEL || !!hotelID

	let customEntityKey =
		isHotel && !isLandmark(hotel as string) ? hotelID : placeID

	// Could not find any use of latlon, commenting for now
	/* if (isLandmark(hotel as string) && latlon) {
		customEntityKey = `latlon:${latlon}`
	}*/

	let userIP = FALLBACK_IP_ADDRESS
	const storedData = localStorage.getItem(INIT_DATA_STORAGE_KEY)

	if (storedData) {
		try {
			const parsedData = JSON.parse(storedData)
			userIP = parsedData.userIP || FALLBACK_IP_ADDRESS
		} catch (error) {
			console.error('Error parsing stored user data:', error)
		}
	}

	let checkIn = params.searchParams['check-in-date']
	let checkOut = params.searchParams['check-out-date']

	if (
		type === SearchPlaceType.HOTEL &&
		needsHotelDateFallback(checkIn, checkOut)
	) {
		const checkInDate = addWeeks(new Date(), HOTEL_DEFAULT_CHECK_IN_WEEKS)
		checkIn = format(checkInDate, DATE_FORMAT_QUERY)
		// Set checkout to check-in + default stay days
		checkOut = format(
			addDays(checkInDate, HOTEL_DEFAULT_STAY_DAYS),
			DATE_FORMAT_QUERY
		)
	}
	// For non-hotel searches or when dates are provided but invalid
	else if (!hasValidDates(checkIn, checkOut)) {
		checkIn = DEFAULT_CHECK_IN_DATE
		checkOut = DEFAULT_CHECK_OUT_DATE
	}
	const language = params.params.lang
	try {
		result = await fetchSearchInPlace({
			userAgent,
			sessionID: searchParams?.['sessionid'] || sessionID,
			clientIP: userIP,
			placeID: isHotel ? hotelID : placeID,
			roomAllocation,
			language,
			currency,
			checkIn,
			checkOut,
			timeZone: timezone,
			onlyComplete: onlyComplete || undefined,
			utcOffsetMinutes: utcOffset,
			place:
				polygon && !isHotel
					? ({ entityKey: polygon + ':900' } as any)
					: {
						name,
						type,
						entityKey: customEntityKey || undefined,
						excludedHotels,
					},
			polygon,
			page: page ? parseInt(page) - 1 : DEFAULT_PAGE,

			// Update only for single hotel requests
			query: isHotel && !customEntityKey ? hotel : undefined,

			s: stars,
			...(getUserRating(guestRating) && { gr: getUserRating(guestRating) }),
			h: hotelChain,
			p: property,
			f: facilities,
			t: themes,
			mn: Number(minPrice) || undefined,
			mx: Number(maxPrice) || undefined,
			sort: {
				by: by || SORT_BY.POPULARITY,
				dir: dir,
			},
			ap: allowedPartners,
		})
	} catch (e) {
		console.log('Fetch hotels', e)
		captureMessage(FETCH_HOTELS_ERROR_MESSAGE)
	}

	if (!result || result === null) {
		return {
			hotels: [],
			page: 0,
			pages: 0,
			totalResults: 0,
			// Synthetic empty response: the search-in-place request failed, so this
			// is a technical failure rather than a genuine "no apartments" result.
			requestFailed: true,
			complete: true,
			refreshInterval: 0,
			lowestTotalRate: 0,
			highestTotalRate: 0,
			lowestNightlyRate: 0,
			highestNightlyRate: 0,
			searchParams: {
				'check-in-date': params.searchParams['check-in-date'],
				'check-out-date': params.searchParams['check-out-date'],
				room: room,
			},
			filters: {
				facilities: {},
				propertyTypes: {},
				starRatings: {},
				guestRatings: {},
				hotelThemes: {},
			},
		}
	} else {
		const priceSymbol = Currencies.symbols.get(result.currencyCode) || ''
		return {
			hotels: result.hotels
				? result.hotels
					.filter((hotel) => {
						if (excludedHotels)
							return !excludedHotels.find((exId) => hotel.id === exId)
						if (hotelID) return hotel.id === hotelID
						return true
					})
					.map((hotel) => {
						return {
							id: hotel.id,
							title: hotel.name,
							image: hotel.image,
							lat: hotel.lat,
							long: hotel.long,
							translatedName: hotel.translatedName,
							largeImage: hotel.largeImage,
							location: hotel.address,
							stars: hotel.starRating,
							rating: hotel.guestRating,
							ratingText: hotel.guestRatingSentiment.split(',')[0],
							bestPriceOn: hotel.bestRate.provider.name,
							provider: hotel.bestRate.provider.name,
							numberOfReviews: hotel.numberOfReviews,
							distanceFromCenter: hotel.distance,
							propertyType: hotel.propertyType,
							price: `${priceSymbol}${formatNumber(
								Math.floor(+hotel.bestRate.nightlyRate)
							)}`,
							currency: result!.currencyCode,
							rawPrice: +hotel.bestRate.nightlyRate,
							totalRate: +hotel.bestRate.totalRate,
							lang: result!.languageCode,
							link: hotel.bestRate.bookURI,
							rawHotelData: hotel,
							rates: hotel.rates.reverse().map((rate) => ({
								...rate,
								providerName: rate.provider.name,
								providerLogo: rate.provider.logo,
								nightlyRate: `${priceSymbol}  ${formatNumber(
									Math.floor(+rate.nightlyRate)
								)}`,
								rawPrice: +rate.nightlyRate,
							})),
						}
					})
				: [],
			page: result.page,
			pages: result.pages,
			totalResults: result.totalResults,
			complete: result.complete,
			lowestTotalRate: Math.floor(result.lowestTotalRate),
			highestTotalRate: Math.floor(result.highestTotalRate),
			lowestNightlyRate: Math.floor(result.lowestNightlyRate),
			highestNightlyRate: Math.floor(result.highestNightlyRate),
			refreshInterval: 500,
			searchParams: {
				'check-in-date': params.searchParams['check-in-date'],
				'check-out-date': params.searchParams['check-out-date'],
				room: room,
			},
			filters: result.filters,
		}
	}
}
